/**
 * Engine initialization and teardown.
 *
 * Relocated from frontend/src/lib/tx-engine/init.ts as part of the auth
 * boundary migration (spec 009 FILE 14 Group E). The SDK core takes the
 * resolved `TxConnectionConfig` + injected logger.
 *
 * Platform-neutral — safe in src/auth/ core.
 */

import type { BlockhashResolverMode } from '../blockchain/blockhash/index.js';
import type { TxConnectionConfig, TxEngineLogger, TxOnForeground } from './config.js';
import { BlockhashCache } from './rpc/blockhash-cache.js';
import { destroyConnection, getConnection, warmConnection } from './rpc/connection.js';
import { PriorityFeeCache } from './rpc/priority-fee-cache.js';

let initialized = false;
let blockhashCache: BlockhashCache | null = null;
let priorityFeeCache: PriorityFeeCache | null = null;

/**
 * Get the shared blockhash cache instance. Throws if the engine has not
 * been initialized — `initTxEngine` constructs the cache with the injected
 * logger / foreground slot.
 */
export function getBlockhashCache(): BlockhashCache {
  if (blockhashCache === null) {
    throw new Error('tx-engine not initialized — call initTxEngine() first');
  }
  return blockhashCache;
}

/**
 * Get the shared priority fee cache instance. Throws if the engine has not
 * been initialized.
 */
export function getPriorityFeeCache(): PriorityFeeCache {
  if (priorityFeeCache === null) {
    throw new Error('tx-engine not initialized — call initTxEngine() first');
  }
  return priorityFeeCache;
}

export interface InitTxEngineArgs {
  connectionConfig: TxConnectionConfig;
  logger: TxEngineLogger;
  onForeground?: TxOnForeground | undefined;
  resolveBlockhashResolverMode?: () => BlockhashResolverMode;
}

/**
 * Initialize the transaction engine.
 * Creates connection, warms it, and starts blockhash cache.
 * Idempotent: calling twice without teardown is safe.
 */
export function initTxEngine(args: InitTxEngineArgs): void {
  if (initialized) {
    return;
  }

  const { connectionConfig, logger, onForeground, resolveBlockhashResolverMode } = args;
  initialized = true;
  blockhashCache = new BlockhashCache(logger, onForeground, resolveBlockhashResolverMode);
  priorityFeeCache = new PriorityFeeCache(logger, connectionConfig.network, onForeground);

  const connection = getConnection(connectionConfig);
  blockhashCache.start(connection);
  priorityFeeCache.start(connection);

  warmConnection(connection, logger).catch(() => {
    // Non-fatal, handled inside warmConnection
  });

  logger.info('Transaction engine initialized');
}

/**
 * Tear down the transaction engine.
 * Stops blockhash cache and destroys connection.
 */
export function teardownTxEngine(logger: TxEngineLogger): void {
  if (!initialized) {
    return;
  }

  blockhashCache?.stop();
  blockhashCache = null;
  priorityFeeCache?.stop();
  priorityFeeCache = null;
  destroyConnection();
  initialized = false;

  logger.info('Transaction engine torn down');
}
